import { Banknote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatBucketDate } from "~/utils/formatting";
import { toIsoDate } from "~/shared/sales-data/sales-date-range";
import type { SummaryCardsData } from "~/pages/product-details/tabs/sales-data-tab/data/build-summary-cards-model";
import { MetricColumn } from "./metric-column";
import { SummaryCard } from "./summary-card";

export const TotalSalesCard = ({
  sales,
  from,
  to,
  isLoading,
  unavailableReason,
  onClick,
}: {
  sales?: SummaryCardsData["sales"];
  from?: Date;
  to?: Date;
  isLoading?: boolean;
  unavailableReason?: string;
  onClick: () => void;
}) => {
  const { t } = useTranslation(["productDetails"]);

  const windowText =
    from && to && !unavailableReason
      ? t("salesData.summaryCards.window", {
          start: formatBucketDate(toIsoDate(from)),
          end: formatBucketDate(toIsoDate(to)),
        })
      : undefined;
  // The peak sub-metric only applies to a present, available series; otherwise
  // the footer falls back to the window range (label-only).
  const peak = unavailableReason ? undefined : sales?.peak;

  return (
    <SummaryCard
      testId="summary-card-sales"
      icon={<Banknote className="h-4 w-4" aria-hidden />}
      title={t("salesData.summaryCards.sales.title")}
      isLoading={isLoading}
      badge={t("salesData.summaryCards.byMarket")}
      body={
        <div className="flex divide-x divide-border">
          {(sales?.perMarket ?? []).map((entry) => (
            <MetricColumn
              key={entry.market}
              colorClassName={entry.colorClassName}
              description={entry.market}
              available={!entry.isUnavailable}
              value={entry.formattedValue}
            />
          ))}
        </div>
      }
      series={(sales?.perMarket ?? []).map((entry) => ({
        key: entry.market,
        color: entry.color,
        points: entry.sparkline,
      }))}
      unavailableReason={unavailableReason}
      detailSummary={
        peak
          ? t("salesData.summaryCards.peak", { date: peak.date })
          : windowText
      }
      detailValue={peak?.perMarketFormatted}
      jumpHint={t("salesData.summaryCards.jumpToChart")}
      onClick={onClick}
    />
  );
};
